import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Film, Lock, MessageSquarePlus, Clock, Send } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Logo } from "@/components/Common";

const fmtTime = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

export default function StoryReview() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("gv_review_name") || "");
  const [text, setText] = useState("");
  const [atTime, setAtTime] = useState(0);
  const [posting, setPosting] = useState(false);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/stories/review/${slug}`); setData(r.data); }
    catch { setErr(true); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const captureTime = () => { const t = videoRef.current?.currentTime || 0; setAtTime(Math.floor(t)); };

  const jumpTo = (t) => { if (videoRef.current) { videoRef.current.currentTime = t; videoRef.current.play().catch(() => {}); } };

  const post = async () => {
    if (!text.trim()) { toast.error("Please write a note"); return; }
    setPosting(true);
    try {
      await api.post(`/stories/review/${slug}/comments`, { t_seconds: atTime, text: text.trim(), author: name.trim() });
      if (name.trim()) localStorage.setItem("gv_review_name", name.trim());
      setText("");
      toast.success("Note added — thank you");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
    finally { setPosting(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))]">This review page isn't available.</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--letterbox))]"><Loader2 className="animate-spin text-white/70" /></div>;

  const comments = data.comments || [];

  return (
    <div className="min-h-screen bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))]" data-testid="story-review-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex justify-center opacity-90"><Logo light /></div>
        <div className="mt-8 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Family review · draft cut</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">{data.title}</h1>
          <p className="mt-2 text-white/60 text-sm">Watch the draft and leave notes at any moment — “swap this photo”, “this bit is perfect”, and so on. The family will see them before the final cut.</p>
        </div>

        {data.state === "closed" && (
          <div className="mt-10 text-center text-white/70" data-testid="review-closed"><Lock className="mx-auto" /><p className="mt-2">This review page is currently closed.</p></div>
        )}

        {data.state === "not_ready" && (
          <div className="mt-10 text-center text-white/70" data-testid="review-not-ready"><Film className="mx-auto" /><p className="mt-2">The draft is still being produced. Please check back soon.</p></div>
        )}

        {data.state === "open" && (
          <>
            <div className="mt-8">
              <video ref={videoRef} src={fileUrl(data.film.file_id)} controls className="w-full rounded-2xl bg-black aspect-video shadow-2xl" data-testid="review-video" />
            </div>

            {/* composer */}
            <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="review-composer">
              <div className="flex items-center gap-2 text-sm font-semibold"><MessageSquarePlus size={17} /> Leave a note</div>
              <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3">
                <input data-testid="review-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)"
                  className="h-11 px-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-white/30" />
                <button data-testid="review-capture-time" onClick={captureTime} className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-xl bg-white/10 border border-white/15 text-sm font-medium hover:bg-white/20">
                  <Clock size={15} /> At {fmtTime(atTime)}
                </button>
              </div>
              <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500}
                placeholder="e.g. Could we swap this photo for the one from the beach?" className="mt-3 w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-white/30" />
              <div className="mt-3 flex justify-end">
                <button data-testid="review-post-button" onClick={post} disabled={posting} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-semibold disabled:opacity-60">
                  {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Add note at {fmtTime(atTime)}
                </button>
              </div>
            </div>

            {/* list */}
            <div className="mt-6" data-testid="review-comments">
              <div className="text-sm font-semibold text-white/70">{comments.length} note{comments.length !== 1 ? "s" : ""}</div>
              <div className="mt-3 space-y-2">
                {comments.map((c) => (
                  <div key={c.comment_id} data-testid={`review-comment-${c.comment_id}`} className="rounded-xl bg-white/5 border border-white/10 p-3.5 flex items-start gap-3">
                    <button onClick={() => jumpTo(c.t_seconds)} className="shrink-0 mt-0.5 font-mono text-xs px-2 py-1 rounded-md bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/30" data-testid={`review-jump-${c.comment_id}`}>{fmtTime(c.t_seconds)}</button>
                    <div className="min-w-0">
                      <div className="text-sm leading-relaxed">{c.text}</div>
                      <div className="mt-1 text-xs text-white/45">{c.author || "Family"}</div>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && <div className="rounded-xl border border-dashed border-white/15 p-6 text-center text-sm text-white/50">Be the first to leave a note.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
